// The way into a closed <AgentSidePanel>: one small button in the chat's
// top-left corner, where the panel opens from.
//
// A tester left a chat for Tables, came back to the blank start screen and
// thought her chat had gone. It was in the panel's History tab, but the only
// way in was an edge pill halfway down the page that showed itself when the
// pointer came near (#1795 follow-up). Adam: "Could it have an icon in the top
// left you click to open, like we have the image options in the bottom right?
// It could unfurl the different options and you click and it opens the panel
// on that tab."
//
// So this is the start screen's cover dial turned on its side: hover or focus
// unfurls the panel's tabs downward, each with its name shown beside it (an
// icon alone does not say "History"), and picking one opens the panel on that
// tab. The button itself opens the panel on its default tab. A touch tap with
// the dial closed unfurls it first; a second tap on the button opens.
//
// Generic on purpose: it takes the same tab list the panel does, History
// first, then the host's tabs in their order.

import React, { useState } from 'react'
import { Box, SpeedDial, SpeedDialAction, type SpeedDialProps, type SxProps, type Theme } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined'
import type { AgentSidePanelTab } from '../plugins.js'
import { SIDE_PANEL_HISTORY_KEY, sidePanelDefaultKey } from './AgentSidePanel.js'
import { HAIRLINE, PILL_RADIUS } from './surface.js'

export interface SidePanelLauncherProps {
  /** The host tabs, as given to <AgentSidePanel>. History is offered first. */
  tabs?: AgentSidePanelTab[]
  /** Open the panel on this tab. The button itself passes the default tab. */
  onOpen: (key: string) => void
  /** True while the panel is open: the launcher has nothing to offer, so it goes. */
  hidden?: boolean
  /** The look of the button and of each tab's button, e.g. glass over a cover
   *  image. Defaults to a paper button that reads on the chat's ground. */
  fabSx?: SxProps<Theme>
  /** Extra styles on the root, after its corner placement (e.g. a fade). */
  sx?: SxProps<Theme>
}

/** A paper button on the chat's ground, edged with the conversation's hairline. */
const DEFAULT_FAB_SX = {
  color: 'text.secondary',
  backgroundColor: 'background.paper',
  border: HAIRLINE,
  boxShadow: 'none',
  '&:hover': { backgroundColor: 'background.paper', color: 'text.primary' },
} as const

/** Visible to screen readers only: turns the label "History" into the name
 *  "Open History" without saying "Open" on screen six times. */
const SR_ONLY_SX = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const

function TabGlyph({ tab }: { tab: AgentSidePanelTab }) {
  if (tab.icon) return <>{tab.icon}</>
  return (
    <Box component="span" aria-hidden sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>
      {tab.label.charAt(0).toUpperCase()}
    </Box>
  )
}

export default function SidePanelLauncher({ tabs = [], onOpen, hidden, fabSx, sx }: SidePanelLauncherProps) {
  const [dialOpen, setDialOpen] = useState(false)

  if (hidden) return null

  const handleLaunch = (key: string) => {
    setDialOpen(false)
    onOpen(key)
  }

  const entries: AgentSidePanelTab[] = [
    { key: SIDE_PANEL_HISTORY_KEY, label: 'History', icon: <HistoryIcon fontSize="small" />, render: () => null },
    ...tabs,
  ]
  const fabLook = fabSx ?? DEFAULT_FAB_SX

  return (
    <SpeedDial
      ariaLabel="Open side panel"
      data-testid="side-panel-launcher"
      direction="down"
      icon={<ViewSidebarOutlinedIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} />}
      open={dialOpen}
      // Hover and focus unfurl; a click on the button while unfurled opens the
      // panel on its default tab. MUI reports that click as a 'toggle' close.
      onOpen={() => setDialOpen(true)}
      onClose={(_e, reason) => {
        if (reason === 'toggle') handleLaunch(sidePanelDefaultKey(tabs))
        else setDialOpen(false)
      }}
      FabProps={{ size: 'small', sx: fabLook, 'data-testid': 'side-panel-launcher-button' } as SpeedDialProps['FabProps']}
      sx={[
        {
          position: 'absolute',
          top: 12,
          left: 12,
          // Over the conversation and the artifacts preview (2), under any popover.
          zIndex: 3,
          '& .MuiSpeedDialAction-fab': fabLook,
          '& .MuiSpeedDialAction-staticTooltipLabel': {
            fontSize: 13,
            lineHeight: '20px',
            whiteSpace: 'nowrap',
            color: 'text.primary',
            px: 1.25,
            py: 0.25,
            borderRadius: PILL_RADIUS,
            border: HAIRLINE,
            boxShadow: 'none',
            cursor: 'pointer',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& .MuiSpeedDial-fab, & .MuiSpeedDialAction-fab, & .MuiSpeedDialAction-staticTooltipLabel': {
              transition: 'none !important',
              transitionDelay: '0ms !important',
            },
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {entries.map(tab => (
        <SpeedDialAction
          key={tab.key}
          icon={<TabGlyph tab={tab} />}
          // The space sits outside the hidden word: a name is built from its
          // parts trimmed, and the line's leading space collapses on screen.
          tooltipTitle={<><Box component="span" sx={SR_ONLY_SX}>Open</Box>{` ${tab.label}`}</>}
          tooltipOpen
          tooltipPlacement="right"
          onClick={() => handleLaunch(tab.key)}
          FabProps={{ 'data-testid': `side-panel-launcher-${tab.key}` } as SpeedDialProps['FabProps']}
        />
      ))}
    </SpeedDial>
  )
}
